// MLS City Filter - restricts search to available MLS region cities for BLS tenants.
// Used by HeroSplash, FilterLocationSearch, and SearchLocationScreen to replace
// Google Places Autocomplete with local city filtering when on a tenant domain.

/**
 * Check if the current domain is an MLS-restricted tenant (Cove, Stellar, etc.)
 * Non-default subdomains that aren't www/app/localhost are BLS tenants.
 */
export function isMlsRestrictedTenant() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  // Need at least 3 parts (subdomain.domain.tld) to have a real subdomain.
  // "gitcha.com" (2 parts) and "localhost" (1 part) are not tenants.
  if (parts.length < 3) return false;
  const subdomain = parts[0].toLowerCase();
  // "local" is the dev prefix for the main Gitcha app (local.gitcha.com)
  return !["www", "app", "local", "localhost", "127"].includes(subdomain);
}

/**
 * Filter available MLS cities by search query.
 * Returns results formatted to match the dropdown structure used by search components.
 *
 * @param {string} query - User's search input
 * @param {Array} availableCities - Redux state.locations.availableCities
 *   Shape: [{ state: "AZ", cities: [{ City: "PHOENIX" }, ...] }, ...]
 * @returns {Array} Matching cities formatted as dropdown items
 */
export function filterCitiesByQuery(query, availableCities) {
  if (!query || !availableCities?.length) return [];

  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const results = [];
  const seen = new Set();

  for (const stateGroup of availableCities) {
    const stateCode = stateGroup.state;
    const cities = stateGroup.cities || [];

    for (const cityObj of cities) {
      const cityName = cityObj.City || "";
      const cityLower = cityName.toLowerCase();
      const fullName = `${cityName}, ${stateCode}`.toLowerCase();

      // Deduplicate by city+state key
      const dedupeKey = `${cityLower}|${stateCode}`;
      if (seen.has(dedupeKey)) continue;

      // Match against city name, state code, or full "city, state" string
      if (
        cityLower.includes(normalizedQuery) ||
        fullName.includes(normalizedQuery) ||
        stateCode.toLowerCase().includes(normalizedQuery)
      ) {
        seen.add(dedupeKey);

        // Format city name to title case for display
        const displayCity = cityName
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");

        results.push({
          _isMlsCity: true,
          city: displayCity,
          cityRaw: cityName,
          state: stateCode,
          formatted_address: `${displayCity}, ${stateCode}`,
        });
      }
    }
  }

  // Sort: exact prefix matches first, then alphabetical
  results.sort((a, b) => {
    const aPrefix = a.city.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
    const bPrefix = b.city.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
    if (aPrefix !== bPrefix) return aPrefix - bPrefix;
    return a.formatted_address.localeCompare(b.formatted_address);
  });

  return results.slice(0, 8);
}
